import { DriverRegistrationPortalNoAuthService } from '@gen/driverRegistration/driverRegistrationPortalNoAuth';
import { useCallback, useEffect, useMemo, useState } from 'react';
import driverRegistrationApiClient from '../../../api/DriverRegistrationApi';

export interface TinPair {
  index: number;
  country?: string;
  tin?: string;
  tinError?: string;
  countryError?: string;
}

interface TinFormatters {
  [
    key: string
  ]: DriverRegistrationPortalNoAuthService.TaxIdentificationNumberFormat;
}

export interface UseTinPairsHandlers {
  changeCountry: (index: number, country: string | undefined) => void;
  setTin: (index: number, tin: string | undefined) => void;
  saveIfEnabledAfterValidation: (index: number) => void;
  addNewTinRow: () => void;
  deleteTinRowAndSaveIfEnabled: (index: number) => void;
}

interface UseTinPairsHookResult {
  tinPairs: TinPair[];
  tinFormatters: TinFormatters;
  handlers: UseTinPairsHandlers;
}

interface TinPairValidationResult {
  pair: TinPair;
  shouldBlur: boolean;
}

interface TinValidationMessages {
  countryRequired: string;
  numberRequired: string;
  numberFormatInvalid: string;
}

const getValidatedTinPair = (
  pair: TinPair,
  tinFormatters: TinFormatters,
  messages: TinValidationMessages
): TinPairValidationResult => {
  if (pair.country && tinFormatters[pair.country]) {
    if (!pair.tin || pair.tin.length < 1) {
      return {
        pair: {
          ...pair,
          countryError: undefined,
          tinError: messages.numberRequired,
        },
        shouldBlur: false,
      };
    }
    const tinFormatter = tinFormatters[pair.country];
    const formatError = !tinFormatter.format_expressions.some((format) => {
      const match = pair.tin?.match(format);
      return !!(match && pair.tin === match[0]);
    });
    if (formatError) {
      return {
        pair: {
          ...pair,
          countryError: undefined,
          tinError: messages.numberFormatInvalid.replace(
            '{examples}',
            tinFormatter.placeholder
          ),
        },
        shouldBlur: false,
      };
    }
    return { pair, shouldBlur: true };
  }

  const countryError = !pair.country ? messages.countryRequired : undefined;
  const tinError = !pair.tin ? messages.numberRequired : undefined;
  return {
    pair: {
      ...pair,
      countryError,
      tinError,
    },
    shouldBlur: !countryError && !tinError,
  };
};

export const useTinPairs = (
  initialTins: TinPair[],
  updateCallback: (newValue: Omit<TinPair, 'index'>[] | null) => void,
  blurCallback: (event: React.FocusEvent<HTMLInputElement, Element>) => void,
  commonTranslations: DriverRegistrationPortalNoAuthService.CommonTranslations,
  defaultCountry: string | undefined
): UseTinPairsHookResult => {
  const [tinFormatters, setTinFormatters] = useState<TinFormatters>({});

  useEffect(() => {
    const fetchData = async () => {
      driverRegistrationApiClient
        .getTaxIdentificationNumberFormats({})
        .then((result) => {
          setTinFormatters(result.list);
        });
    };

    fetchData().catch(); // Silently ignore, use empty tinFormatter list.
  }, []);

  const updateHandler = useCallback(
    (newValue: TinPair[] | null) => {
      if (!newValue) {
        updateCallback(null);
        return;
      }
      updateCallback(
        newValue.map((tinPair) => ({
          country: tinPair.country,
          tin: tinPair.tin,
        }))
      );
    },
    [updateCallback]
  );

  const tinsForState = (initialTins ?? []).map((pair, index) => ({
    ...pair,
    index,
  }));
  const [tinPairs, setTinPairs] = useState(
    tinsForState.length
      ? tinsForState
      : ([
          {
            tin: undefined,
            country: defaultCountry,
            index: 0,
          },
        ] as TinPair[])
  );

  const updateTinPairAtIndex = useCallback(
    (index: number, resolvePair: (current: TinPair) => TinPair) => {
      setTinPairs((prev) => {
        const current = prev[index];
        if (!current) {
          return prev;
        }
        const newTins = [
          ...prev.slice(0, index),
          resolvePair(current),
          ...prev.slice(index + 1),
        ];
        updateHandler(newTins);
        return newTins;
      });
    },
    [updateHandler]
  );

  const validationMessages = useMemo(
    () => ({
      countryRequired:
        commonTranslations.tax_identification_numbers.country_required,
      numberRequired:
        commonTranslations.tax_identification_numbers.number_required,
      numberFormatInvalid:
        commonTranslations.tax_identification_numbers.number_format_invalid,
    }),
    [
      commonTranslations.tax_identification_numbers.country_required,
      commonTranslations.tax_identification_numbers.number_format_invalid,
      commonTranslations.tax_identification_numbers.number_required,
    ]
  );

  const saveIfEnabledAfterValidation = useCallback(
    (index: number) => {
      const blurAfterSave = { value: false };
      setTinPairs((prev) => {
        const current = prev[index];
        if (!current) {
          return prev;
        }
        const result = getValidatedTinPair(
          current,
          tinFormatters,
          validationMessages
        );
        blurAfterSave.value = result.shouldBlur;
        const newTins = [
          ...prev.slice(0, index),
          result.pair,
          ...prev.slice(index + 1),
        ];
        updateHandler(newTins);
        return newTins;
      });
      if (blurAfterSave.value) {
        blurCallback({} as React.FocusEvent<HTMLInputElement, Element>);
      }
    },
    [blurCallback, tinFormatters, updateHandler, validationMessages]
  );

  const handlers = useMemo(
    () => ({
      changeCountry: (index: number, country: string | undefined) => {
        setTinPairs((prev) => {
          const current = prev[index];
          if (!current) {
            return prev;
          }
          const withCountry: TinPair = {
            index: current.index,
            country,
            tin: current.tin,
          };
          const result = getValidatedTinPair(
            withCountry,
            tinFormatters,
            validationMessages
          );
          const newTins = [
            ...prev.slice(0, index),
            result.pair,
            ...prev.slice(index + 1),
          ];
          updateHandler(newTins);
          blurCallback({} as React.FocusEvent<HTMLInputElement, Element>);
          return newTins;
        });
      },
      setTin: (index: number, tin: string | undefined) => {
        updateTinPairAtIndex(index, (current) => ({
          index: current.index,
          country: current.country,
          tin,
        }));
      },
      saveIfEnabledAfterValidation,
      addNewTinRow: () => {
        setTinPairs((prev) => {
          const indexes = prev.map((pair) => pair.index);
          const newIndex = Math.max(...indexes) + 1;
          const newTins = [
            ...prev,
            {
              country: defaultCountry,
              tin: undefined,
              index: newIndex,
            },
          ];
          updateHandler(newTins);
          return newTins;
        });
      },
      deleteTinRowAndSaveIfEnabled: (index: number) => {
        setTinPairs((prev) => {
          const newTins = [...prev.slice(0, index), ...prev.slice(index + 1)];
          updateHandler(newTins.length ? newTins : null);
          return newTins;
        });
        blurCallback({} as React.FocusEvent<HTMLInputElement, Element>);
      },
    }),
    [
      blurCallback,
      defaultCountry,
      saveIfEnabledAfterValidation,
      tinFormatters,
      updateHandler,
      updateTinPairAtIndex,
      validationMessages,
    ]
  );

  return { tinPairs, tinFormatters, handlers };
};
